/* eslint-disable react/prop-types */
/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { motion } from 'framer-motion';
import { keyframes, styled } from 'styled-components';

import foxImage from 'assets/img/goodie-fox.webp';
import AuroraBackground from 'pages/HomeV2/components/Background';
import { GhostButton, PrimaryButton } from 'pages/HomeV2/components/Buttons';
import { CALENDLY_URL, Container, v2 } from 'pages/HomeV2/v2';

import { BellIcon, CalendarIcon, ClipboardIcon, DocumentIcon } from '../icons';
import { GOODIE_EVENTS, trackGoodieEvent } from '../tracking';

const Wrapper = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 116px 0 48px;
  overflow: hidden;
  background: ${v2.bg};

  @media (min-width: 1025px) {
    min-height: min(100vh, 920px);
    padding: 150px 0 70px;
  }
`;

const HeroGrid = styled(Container)`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 40px;
  text-align: center;

  @media (min-width: 1025px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    gap: clamp(32px, 4vw, 64px);
    text-align: left;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 22px;

  @media (min-width: 1025px) {
    align-items: flex-start;
    gap: 26px;
  }
`;

/* Same pill as the start page's hero badge, without the live indicator. */
const Greeting = styled(motion.p)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 9px 16px;
  border-radius: 100px;
  border: 1px solid ${v2.border};
  background: ${v2.surface};
  backdrop-filter: blur(12px);
  font-family: ${v2.fontBody};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  color: ${v2.textMuted};
  text-align: left;

  > span {
    /* Even two-line split on narrow phones instead of an orphaned "ist. 🦊". */
    text-wrap: balance;
  }

  .nowrap {
    white-space: nowrap;
  }

  &::before {
    content: '';
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${v2.orange};
    box-shadow: 0 0 12px ${v2.orange};
  }

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 8px 14px;
  }
`;

const Headline = styled.h1`
  font-family: ${v2.fontDisplay};
  font-size: clamp(2.1rem, 4.7vw, 3.55rem);
  font-weight: 800;
  line-height: 1.06;
  letter-spacing: -0.03em;
  color: ${v2.text};
  margin: 0;
  text-wrap: balance;

  .line {
    display: block;
    overflow: hidden;
    /* Leave room for descenders inside the reveal mask. */
    padding-bottom: 0.14em;
    margin-bottom: -0.14em;
  }

  .grad {
    background: ${v2.gradient};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
`;

const Sub = styled(motion.p)`
  font-family: ${v2.fontBody};
  font-size: clamp(1.05rem, 1.45vw, 1.22rem);
  line-height: 1.6;
  color: ${v2.textMuted};
  max-width: 560px;
  margin: 0;
`;

const Ctas = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 4px;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;

    a {
      width: 100%;
    }
  }

  @media (min-width: 1025px) {
    justify-content: flex-start;
    gap: 16px;
  }
`;

const Microcopy = styled(motion.p)`
  margin: -6px 0 0;
  font-family: ${v2.fontBody};
  font-size: 13.5px;
  color: ${v2.textFaint};
`;

/* ---------- Visual: Dr. FOXI with floating feature chips ---------- */

const lift = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`;

const floor = keyframes`
  0%, 100% { transform: translateX(-50%) scaleX(1); opacity: 0.55; }
  50% { transform: translateX(-50%) scaleX(0.86); opacity: 0.3; }
`;

const Visual = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  height: 340px;

  @media (min-width: 600px) {
    height: 400px;
  }

  @media (min-width: 1025px) {
    max-width: none;
    height: 560px;
    margin: 0;
  }
`;

const Glow = styled.div`
  position: absolute;
  left: 50%;
  bottom: 6%;
  width: 90%;
  aspect-ratio: 1;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(
      circle at 45% 40%,
      rgba(255, 159, 79, 0.34),
      transparent 60%
    ),
    radial-gradient(circle at 60% 70%, rgba(30, 150, 252, 0.3), transparent 62%);
  filter: blur(46px);
  pointer-events: none;
`;

const FloorShadow = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 60%;
  height: 40px;
  border-radius: 50%;
  background: radial-gradient(
    ellipse at center,
    rgba(0, 0, 0, 0.65),
    transparent 72%
  );
  filter: blur(16px);
  transform: translateX(-50%);
  animation: ${floor} 7s ease-in-out infinite;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Fox = styled.img`
  position: absolute;
  left: 50%;
  bottom: 12px;
  height: 92%;
  width: auto;
  max-width: none;
  transform: translateX(-50%);
  filter: drop-shadow(0 24px 40px rgba(0, 0, 0, 0.45));
  /* Soften the hard bottom edge of the artwork. */
  -webkit-mask-image: linear-gradient(to bottom, #000 84%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 84%, transparent 100%);
  animation: ${lift} 7s ease-in-out infinite;
  will-change: transform;
  user-select: none;
  -webkit-user-drag: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Chip = styled(motion.div)`
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 10px 14px 10px 10px;
  border-radius: 100px;
  border: 1px solid ${v2.borderStrong};
  background: rgba(14, 24, 40, 0.82);
  backdrop-filter: blur(14px);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.35);
  font-family: ${v2.fontBody};
  font-size: 13.5px;
  font-weight: 600;
  color: ${v2.text};
  white-space: nowrap;
  z-index: 2;

  span.icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ $accent }) =>
      $accent === 'orange' ? v2.orangeSoft : v2.blueSoft};
    color: ${({ $accent }) => ($accent === 'orange' ? v2.orange : '#5bb8ff')};

    svg {
      width: 16px;
      height: 16px;
    }
  }

  &.chip-1 {
    top: 6%;
    left: 0;
  }

  &.chip-2 {
    top: 24%;
    right: 0;
  }

  &.chip-3 {
    bottom: 26%;
    left: 0;
  }

  &.chip-4 {
    bottom: 12%;
    right: 2%;
  }

  /* Below the two-column layout the fox is smaller: keep the chips next to
     the ears and the coat instead of in front of the face. */
  @media (max-width: 1024px) {
    &.chip-2 {
      top: 52%;
    }

    &.chip-3 {
      bottom: 24%;
    }
  }

  @media (max-width: 599px) {
    font-size: 12.5px;
    padding: 8px 12px 8px 8px;

    &.chip-3 {
      bottom: 6%;
    }

    &.chip-4 {
      display: none;
    }
  }
`;

const lineAnim = {
  hidden: { y: '110%' },
  visible: (custom) => ({
    y: '0%',
    transition: { duration: 0.9, delay: 0.3 + custom * 0.12, ease: v2.ease },
  }),
};

const chipAnim = (delay) => ({
  initial: { opacity: 0, y: 16, scale: 0.94 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.7, delay, ease: v2.ease },
});

const CHIPS = [
  {
    className: 'chip-1',
    icon: ClipboardIcon,
    label: 'Anamnese digital',
    accent: 'blue',
  },
  {
    className: 'chip-2',
    icon: CalendarIcon,
    label: 'Termin online gebucht',
    accent: 'blue',
  },
  {
    className: 'chip-3',
    icon: DocumentIcon,
    label: 'Abrechnung erledigt',
    accent: 'orange',
  },
  {
    className: 'chip-4',
    icon: BellIcon,
    label: 'Recall erinnert',
    accent: 'orange',
  },
];

const GoodieHero = ({ onScrollTo }) => (
  <Wrapper aria-labelledby="goodie-hero-title">
    <AuroraBackground withParticles={false} />
    <HeroGrid>
      <Copy>
        <Greeting
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: v2.ease }}
        >
          <span>
            Schön, dass unsere <span className="nowrap">Goodie-Bag</span>{' '}
            angekommen ist. <span aria-hidden>🦊</span>
          </span>
        </Greeting>
        <Headline id="goodie-hero-title">
          <span className="line">
            <motion.span
              style={{ display: 'inline-block' }}
              variants={lineAnim}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              Die Nervennahrung übernehmen wir.
            </motion.span>
          </span>
          <span className="line">
            <motion.span
              style={{ display: 'inline-block' }}
              variants={lineAnim}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              Den Praxisalltag erleichtert <span className="grad">FOXI</span>.
            </motion.span>
          </span>
        </Headline>
        <Sub
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: v2.ease }}
        >
          FOXI verbindet Dokumentation, Abrechnung, digitale Anamnese,
          Terminbuchung und Recall in einer modernen Software für
          Zahnarztpraxen.
        </Sub>
        <Ctas
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: v2.ease }}
        >
          <PrimaryButton
            href={CALENDLY_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              trackGoodieEvent(GOODIE_EVENTS.demoClick, { placement: 'hero' })
            }
          >
            FOXI kostenlos kennenlernen
          </PrimaryButton>
          <GhostButton
            href="#features"
            onClick={(event) => {
              event.preventDefault();
              onScrollTo('features');
            }}
          >
            Funktionen ansehen
          </GhostButton>
        </Ctas>
        <Microcopy
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
        >
          Unverbindliche Demo · speziell für Zahnarztpraxen
        </Microcopy>
      </Copy>

      <Visual
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.45, ease: v2.ease }}
      >
        <Glow aria-hidden />
        <FloorShadow aria-hidden />
        <Fox
          src={foxImage}
          alt="Dr. FOXI – der Fuchs im Arztkittel, Maskottchen der FOXI Software"
          width="640"
          height="1090"
          decoding="async"
        />
        {CHIPS.map((chip, index) => {
          const Icon = chip.icon;
          return (
            <Chip
              key={chip.className}
              className={chip.className}
              $accent={chip.accent}
              aria-hidden
              {...chipAnim(0.9 + index * 0.12)}
            >
              <span className="icon">
                <Icon />
              </span>
              {chip.label}
            </Chip>
          );
        })}
      </Visual>
    </HeroGrid>
  </Wrapper>
);

export default GoodieHero;
